import styled, { css } from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui/currency-icon'
import { premiumFontCss, TierIcon } from '@root/entities/premium/ui'
import { mediaMaxMobile } from '@root/shared/ui-kit/theme'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumTier } from '@root/entities/premium'
import type { ClassNameProps } from '@root/shared/react'

type SplashPointsCurrencyProps = {
  currency: CurrencyModel
  splashPointsAmount: number
  tier: PremiumTier | undefined
} & ClassNameProps

export const SplashPointsCurrency: React.FC<SplashPointsCurrencyProps> = ({
  className,
  currency,
  splashPointsAmount,
  tier,
}) => {
  return (
    <Wrapper className={className}>
      <LeftSeparator />
      <Root>
        <CurrencyIcon
          css={css`
            margin-right: 4px;
          `}
          size={34}
          currency={currency}
          withNetwork
        />
        <Middle>
          <Title ellipsis>{currency.title}</Title>
          <Symbol ellipsis>{currency.ticker}</Symbol>
        </Middle>
        <TierContainer>
          <TierIcon tier={tier} />
          <div>
            <Numeric value={splashPointsAmount} preset="splash-points" />
          </div>
        </TierContainer>
      </Root>
      <RightSeparator />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;

  ${mediaMaxMobile(css`
    margin-bottom: 10px;
  `)}
`

const LeftSeparator = styled.div`
  height: 1px;
  width: 65px;
  background: linear-gradient(90deg, transparent 0%, var(--backgroundA-3) 100%);
`
const RightSeparator = styled.div`
  height: 1px;
  width: 65px;
  background: linear-gradient(270deg, transparent 0%, var(--background-3) 100%);
`

const Root = styled.div`
  border-radius: 10px;
  border: 1px solid var(--backgroundA-3);
  background: var(--backgroundA-3);

  padding-block: 5px;
  padding-inline: 10px;

  min-width: 188px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px;
`

const Title = styled(Typography)`
  ${premiumFontCss}

  color: var(--text-12);

  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const Symbol = styled(Typography)`
  color: var(--text-11, #b4b4b4);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const Middle = styled.div``

const TierContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  align-self: start;
  margin-top: 3px;
  gap: 2px;

  color: var(--text-12);
  text-align: right;
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
`
